import type React from "react";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { parseScopedThreadKey, scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import { readEnvironmentApi } from "../../environmentApi";
import { newCommandId, newMessageId } from "../../lib/utils";
import { toastManager } from "../ui/toast";
import { useUiStateStore } from "../../uiStateStore";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import type { SidebarThreadSummary } from "../../types";
import type { useThreadActions } from "../../hooks/useThreadActions";
import type { ThreadShelfActions } from "../../hooks/useThreadShelfActions";
import { selectThreadByRef } from "../../store";
import { useDestructiveConfirm } from "./useDestructiveConfirm";
import { startFreshProviderSession } from "./startFreshProviderSession";
import { type ThreadActionMenuId } from "./threadActionMenu.logic";
// ---------------------------------------------------------------------------
// useSidebarThreadHandlers — thread-row interaction callbacks
// ---------------------------------------------------------------------------

/**
 * The thread actions the sidebar's context menus perform.
 *
 * Split out of `useSidebarThreadHandlers`, which had grown to 734 lines while
 * `.agents/rules/frontend-architecture.md` requires extraction before adding to
 * a component file already past 300. These were already module-level functions
 * taking explicit params, so this is a file split rather than a redesign — and
 * it puts the orchestration (environment routing, command dispatch, failure
 * handling, selection mutation) somewhere it can be read and tested without the
 * hook around it.
 *
 * @module sidebarThreadActions
 */

export interface RegenerateSelectedTitlesParams {
  readonly regenerationIdleKeys: readonly string[];
  readonly sidebarThreadByKeyRef: React.RefObject<ReadonlyMap<string, SidebarThreadSummary>>;
  readonly clearSelection: () => void;
  readonly removeFromSelection: (keys: string[]) => void;
}

export interface DeleteSelectedThreadsParams {
  readonly threadKeys: readonly string[];
  readonly count: number;
  readonly confirmThreadDelete: boolean;
  readonly deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  readonly destructiveConfirm: ReturnType<typeof useDestructiveConfirm>;
  readonly sidebarThreadByKeyRef: React.RefObject<ReadonlyMap<string, SidebarThreadSummary>>;
  readonly removeFromSelection: (keys: string[]) => void;
}

export interface ThreadMenuActionParams {
  readonly clicked: ThreadActionMenuId;
  readonly threadRef: ScopedThreadRef;
  readonly thread: SidebarThreadSummary;
  readonly threadKey: string;
  readonly threadWorkspacePath: string | null;
  readonly retryPromptText: string | null;
  readonly threadDetail: ReturnType<typeof selectThreadByRef> | null;
  readonly confirmThreadDelete: boolean;
  readonly copyPathToClipboard: ReturnType<
    typeof useCopyToClipboard<{ path: string }>
  >["copyToClipboard"];
  readonly copyThreadIdToClipboard: ReturnType<
    typeof useCopyToClipboard<{ threadId: string }>
  >["copyToClipboard"];
  readonly copyBranchToClipboard: ReturnType<
    typeof useCopyToClipboard<{ branch: string }>
  >["copyToClipboard"];
  /** Starts a new thread in the same project, seeded with a branch. */
  readonly startNewThreadOnBranch: (branch: string) => void;
  readonly deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  readonly destructiveConfirm: ReturnType<typeof useDestructiveConfirm>;
  readonly markThreadUnread: (threadKey: string, completedAt?: string) => void;
  readonly setRenamingThreadKey: React.Dispatch<React.SetStateAction<string | null>>;
  readonly setRenamingTitle: (title: string) => void;
  readonly setThreadPinned: (threadKey: string, pinned: boolean) => void;
  readonly renamingCommittedRef: React.MutableRefObject<boolean>;
  readonly attemptArchiveThread: ThreadShelfActions["attemptArchiveThread"];
  /** Reverses an archive; one slot with `archive`, so only one of the two ever fires. */
  readonly attemptUnarchiveThread: ThreadShelfActions["attemptUnarchiveThread"];
  readonly attemptSettleThread: ThreadShelfActions["attemptSettleThread"];
  /** Reverses a settle from the row menu; same shape as `attemptUnarchiveThread`. */
  readonly attemptUnsettleThread: ThreadShelfActions["attemptUnsettleThread"];
}

/**
 * One thread's regeneration request. Returns whether it dispatched.
 *
 * Separate from the loop so the batch reads as "for each key, try, record the
 * outcome" — the routing and the error surface are this function's business,
 * and the loop's business is the bookkeeping the three invariants depend on.
 * A missing runtime is a failure rather than a silent skip: the thread was
 * counted in the menu, so the user is owed an outcome for it.
 */
async function requestThreadTitleRegeneration(threadKey: string): Promise<boolean> {
  const threadRef = parseScopedThreadKey(threadKey);
  const environmentApi = threadRef ? readEnvironmentApi(threadRef.environmentId) : null;
  if (!threadRef || !environmentApi) return false;
  try {
    await environmentApi.orchestration.dispatchCommand({
      type: "thread.meta.update",
      commandId: newCommandId(),
      threadId: threadRef.threadId,
      regenerateTitle: true,
    });
    return true;
  } catch (error) {
    toastManager.add({
      type: "error",
      title: "Failed to regenerate thread title",
      description: error instanceof Error ? error.message : "An error occurred.",
    });
    return false;
  }
}

/**
 * Exported for test only. The three rules that make a bulk regeneration correct
 * — sequential dispatch, a key set that only shrinks, and partial-failure
 * accounting — are invisible from the hook's public surface, and each of them
 * was a review finding before it was a test.
 */
export async function regenerateSelectedTitles({
  regenerationIdleKeys,
  sidebarThreadByKeyRef,
  clearSelection,
  removeFromSelection,
}: RegenerateSelectedTitlesParams): Promise<void> {
  // Sequential rather than `Promise.all`, for ordering and per-thread failure
  // isolation — NOT because it bounds the provider reactor. It does not, and
  // the earlier version of this comment claimed otherwise.
  //
  // `dispatch` resolves once `OrchestrationEngine` has accepted and sequenced
  // the command (it awaits a `Deferred` carrying `{ sequence }`), while
  // `ProviderCommandReactor` independently does `worker.enqueue(event)` into a
  // `makeDrainableWorker`. So awaiting each dispatch costs microseconds and the
  // whole selection still reaches that serial worker long before the first
  // model call finishes.
  //
  // That used to mean a Stop queued afterwards sat behind every selected
  // title. It no longer does: title regeneration has its own serial queue in
  // `ProviderCommandReactor`, separate from the one serving turn starts,
  // interrupts, approval responses and session stops. The bound lives there,
  // where it can be enforced — not here, where awaiting a dispatch only ever
  // looked like one.
  const failedKeys: string[] = [];
  const succeededKeys: string[] = [];

  for (const threadKey of regenerationIdleKeys) {
    // Only ever shrink the captured set: a thread that STARTED regenerating
    // while the menu was open is skipped, because a second request would just
    // supersede the first.
    const thread = sidebarThreadByKeyRef.current?.get(threadKey);
    if (!thread || thread.titleRegeneration != null) continue;
    const dispatched = await requestThreadTitleRegeneration(threadKey);
    (dispatched ? succeededKeys : failedKeys).push(threadKey);
  }

  // Only clear what actually dispatched — failures stay selected for an
  // easy retry, exactly as the bulk delete below does.
  if (failedKeys.length === 0) {
    clearSelection();
  } else if (succeededKeys.length > 0) {
    removeFromSelection(succeededKeys);
  }
}

// fallow-ignore-next-line complexity
export async function deleteSelectedThreads({
  threadKeys,
  count,
  confirmThreadDelete,
  deleteThread,
  destructiveConfirm,
  sidebarThreadByKeyRef,
  removeFromSelection,
}: DeleteSelectedThreadsParams): Promise<void> {
  if (confirmThreadDelete) {
    const confirmed = await destructiveConfirm.confirm({
      title: `Delete ${count} thread${count === 1 ? "" : "s"}?`,
      description: "This permanently clears conversation history for these threads.",
      confirmLabel: count === 1 ? "Delete thread" : `Delete ${count} threads`,
    });
    if (!confirmed) return;
  }

  const deletedThreadKeys = new Set(threadKeys);
  const succeededKeys: string[] = [];
  let failedCount = 0;
  for (const threadKey of threadKeys) {
    const thread = sidebarThreadByKeyRef.current?.get(threadKey);
    if (!thread) continue;
    try {
      await deleteThread(scopeThreadRef(thread.environmentId, thread.id), {
        deletedThreadKeys,
        threadSnapshot: thread,
      });
      succeededKeys.push(threadKey);
    } catch {
      // Per-iteration isolation: a single failed deletion must not abort the
      // rest of the batch. Leave the failed thread selected so the user can
      // retry, and report the count once after the loop.
      failedCount += 1;
    }
  }
  // Only clear the selection for threads that actually deleted — failed ones
  // stay selected for an easy retry.
  if (succeededKeys.length > 0) removeFromSelection(succeededKeys);
  if (failedCount > 0) {
    toastManager.add({
      type: "error",
      title: `Failed to delete ${failedCount} thread${failedCount === 1 ? "" : "s"}`,
      description: "Some threads could not be deleted. They remain selected so you can retry.",
    });
  }
}

async function regenerateSingleThreadTitle(
  threadRef: ScopedThreadRef,
  thread: SidebarThreadSummary,
): Promise<void> {
  // The menu item is already disabled while one is running; this guard covers
  // the case where the state changed after the menu opened.
  if (thread.titleRegeneration != null) return;
  await requestThreadTitleRegeneration(scopedThreadKey(threadRef));
}

async function stopThreadRuntime(
  threadRef: ScopedThreadRef,
  thread: SidebarThreadSummary,
): Promise<void> {
  const environmentApi = readEnvironmentApi(threadRef.environmentId);
  if (!environmentApi) return;
  const createdAt = new Date().toISOString();
  try {
    if (thread.session?.activeTurnId && thread.session.activeTurnId.trim().length > 0) {
      await environmentApi.orchestration.dispatchCommand({
        type: "thread.turn.interrupt",
        commandId: newCommandId(),
        threadId: threadRef.threadId,
        turnId: thread.session.activeTurnId,
        createdAt,
      });
      return;
    }
    await environmentApi.orchestration.dispatchCommand({
      type: "thread.session.stop",
      commandId: newCommandId(),
      threadId: threadRef.threadId,
      createdAt,
    });
  } catch (error) {
    toastManager.add({
      type: "error",
      title: "Unable to stop thread",
      description: error instanceof Error ? error.message : "An error occurred.",
    });
  }
}

// fallow-ignore-next-line complexity
async function startThreadTurnFromMenu({
  clicked,
  threadRef,
  thread,
  threadDetail,
  retryPromptText,
}: Pick<
  ThreadMenuActionParams,
  "clicked" | "threadRef" | "thread" | "threadDetail" | "retryPromptText"
>): Promise<void> {
  const environmentApi = readEnvironmentApi(threadRef.environmentId);
  if (!environmentApi) return;
  const interactionMode = threadDetail?.interactionMode ?? thread.interactionMode ?? "default";
  const runtimeMode = threadDetail?.runtimeMode ?? "approval-required";
  const text =
    clicked === "runtime-retry"
      ? (retryPromptText ?? "")
      : "Continue the current task and report concrete progress.";
  if (text.trim().length === 0) {
    toastManager.add({
      type: "warning",
      title: "No prompt to retry",
      description: "This thread has no prior user message to retry.",
    });
    return;
  }
  try {
    await environmentApi.orchestration.dispatchCommand({
      type: "thread.turn.start",
      commandId: newCommandId(),
      threadId: threadRef.threadId,
      interactionMode,
      runtimeMode,
      message: {
        messageId: newMessageId(),
        role: "user",
        text,
        attachments: [],
      },
      createdAt: new Date().toISOString(),
    });
  } catch (error) {
    toastManager.add({
      type: "error",
      title: clicked === "runtime-retry" ? "Retry failed" : "Continue failed",
      description: error instanceof Error ? error.message : "An error occurred.",
    });
  }
}

// fallow-ignore-next-line complexity
export async function runThreadMenuAction(params: ThreadMenuActionParams): Promise<void> {
  const { clicked, threadRef, thread, threadKey } = params;

  if (clicked === "rename") {
    params.setRenamingThreadKey(threadKey);
    params.setRenamingTitle(thread.title);
    params.renamingCommittedRef.current = false;
    return;
  }
  if (clicked === "regenerate-title") {
    await regenerateSingleThreadTitle(threadRef, thread);
    return;
  }
  if (clicked === "mark-unread") {
    params.markThreadUnread(threadKey, thread.latestTurn?.completedAt ?? undefined);
    return;
  }
  if (clicked === "toggle-pin") {
    const currentlyPinned = useUiStateStore.getState().pinnedThreadById[threadKey] === true;
    params.setThreadPinned(threadKey, !currentlyPinned);
    return;
  }
  if (clicked === "runtime-stop") {
    await stopThreadRuntime(threadRef, thread);
    return;
  }
  if (clicked === "runtime-start-fresh") {
    await startFreshProviderSession({
      threadRef,
      confirm: params.destructiveConfirm.confirm,
    });
    return;
  }
  if (clicked === "runtime-continue" || clicked === "runtime-retry") {
    await startThreadTurnFromMenu(params);
    return;
  }
  if (clicked === "copy-path") {
    if (!params.threadWorkspacePath) {
      toastManager.add({
        type: "error",
        title: "Path unavailable",
        description: "This thread does not have a project path to copy.",
      });
      return;
    }
    params.copyPathToClipboard(params.threadWorkspacePath, { path: params.threadWorkspacePath });
    return;
  }
  if (clicked === "copy-branch") {
    // The menu only offers this when the thread has a branch, so reaching here
    // without one means the thread changed under an open menu — say so rather
    // than copying an empty string the user would paste as nothing.
    const branch = thread.branch?.trim() ?? "";
    if (branch.length === 0) {
      toastManager.add({
        type: "error",
        title: "Branch unavailable",
        description: "This thread no longer has a branch to copy.",
      });
      return;
    }
    params.copyBranchToClipboard(branch, { branch });
    return;
  }
  if (clicked === "new-thread-on-branch") {
    const branch = thread.branch?.trim() ?? "";
    if (branch.length === 0) return;
    params.startNewThreadOnBranch(branch);
    return;
  }
  if (clicked === "copy-thread-id") {
    params.copyThreadIdToClipboard(thread.id, { threadId: thread.id });
    return;
  }
  if (clicked === "archive") {
    await params.attemptArchiveThread(threadRef, thread);
    return;
  }
  if (clicked === "unarchive") {
    await params.attemptUnarchiveThread(threadRef, thread);
    return;
  }
  if (clicked === "settle" || clicked === "unsettle") {
    await (clicked === "settle"
      ? params.attemptSettleThread(threadRef, thread)
      : params.attemptUnsettleThread(threadRef, thread));
    return;
  }
  if (clicked !== "delete") return;
  if (params.confirmThreadDelete) {
    const confirmed = await params.destructiveConfirm.confirm({
      title: `Delete thread "${thread.title}"?`,
      description: "This permanently clears conversation history for this thread.",
      confirmLabel: "Delete thread",
    });
    if (!confirmed) return;
  }
  try {
    await params.deleteThread(threadRef, { threadSnapshot: thread });
  } catch (error) {
    toastManager.add({
      type: "error",
      title: "Failed to delete thread",
      description: error instanceof Error ? error.message : "An error occurred.",
    });
  }
}
